import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { rolesOf } from '../lib/permissions';
import type { Role } from '../lib/types';
import { stockMovementType } from '../lib/format';
import { ConfirmDialog } from './ConfirmDialog';

export interface RecordField {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'select' | 'checkbox';
  options?: string[];
  required?: boolean;
}

export interface RecordRow {
  id: string;
  [key: string]: unknown;
}

interface DeleteBlockerSample {
  id?: string | null;
  number?: string | null;
  movement_type?: string | null;
  work_order?: string | null;
}
interface DeleteBlocker {
  table: string;
  label: string;
  count: number;
  samples?: DeleteBlockerSample[];
}

const EDIT_DETAIL_ENDPOINT: Record<string, string> = {
  quotations: '/api/quotations',
  contracts: '/api/contracts',
  deliveries: '/api/deliveries',
  'purchase-orders': '/api/purchase-orders',
};

function detailText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

// 删除被 409 拦下时的详情区：只回显一句话会让管理员全库翻找「到底是哪几张单」。
// 后端 deleteEditableRecord 现在把命中的引用样例放进 details.blockers（库存流水明细
// 带流水号 + 来源工单号），这里逐条渲染并生成「库存流水」页的深链，直接点过去处理。
export function DeleteBlockedDetail({ error }: { error: unknown }) {
  const msg = error instanceof ApiError ? error.message : '删除失败';
  const blockers: DeleteBlocker[] = (() => {
    const d = error instanceof ApiError ? error.details : undefined;
    if (d && typeof d === 'object' && Array.isArray((d as { blockers?: unknown }).blockers)) {
      return (d as { blockers: DeleteBlocker[] }).blockers;
    }
    return [];
  })();
  if (!blockers.length) return <span style={{ color: 'var(--bad)' }}>{msg}</span>;
  return (
    <div>
      <div style={{ color: 'var(--bad)', marginBottom: 8 }}>{msg}</div>
      {blockers.map((b, i) => (
        <div key={i} style={{ marginBottom: b.samples?.length ? 8 : 0 }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 2 }}>{b.label}（{b.count} 条）</div>
          {b.samples?.length ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {(b.samples || []).map((s, j) => (
                <li key={s.id ?? j} style={{ fontSize: 12.5, lineHeight: 1.7 }}>
                  {s.number
                    ? <Link className="num" to={`/stock?tab=ledger&q=${encodeURIComponent(s.number)}`}>{s.number}</Link>
                    : <span className="num muted">{(s.id || '').slice(0, 8)}…</span>}
                  {s.movement_type ? ` · ${stockMovementType(s.movement_type).label}` : ''}
                  {s.work_order ? (
                    <> · 来源工单 <Link className="num" to={`/stock?tab=ledger&q=${encodeURIComponent(s.work_order)}`}>{s.work_order}</Link></>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
      {blockers.some((b) => b.table === 'stock_movement_lines') ? (
        <div style={{ marginTop: 4, fontSize: 12.5 }}>
          <Link to="/stock?tab=ledger">查看关联流水（库存 → 库存流水）</Link>
        </div>
      ) : null}
    </div>
  );
}

// 行级编辑 / 删除：走通用且仅管理员的后端路由 /api/records/:type/:id。
// 非管理员不渲染任何按钮；删除前二次确认，后端做引用与状态校验并写审计。
export function RecordActions<T extends { id: string }>({ type, row, fields, label, onDone, hideEdit = false, context = [], editNote }: {
  type: string;
  row: T;
  fields: RecordField[];
  label: string;
  onDone: () => void;
  hideEdit?: boolean;
  context?: Array<{ label: string; value: unknown }>;
  editNote?: string;
}) {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  // 保留原始错误对象：ApiError.details.blockers 是 409 拦截详情的数据源，降级成字符串会丢
  const [err, setErr] = useState<unknown>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [itemGroups, setItemGroups] = useState<string[]>([]);
  const [documentDetail, setDocumentDetail] = useState<Record<string, unknown> | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  if (!isAdmin) return null;

  const cell = (key: string): unknown => (row as Record<string, unknown>)[key];

  const startEdit = () => {
    const d: Record<string, unknown> = {};
    for (const f of fields) d[f.key] = cell(f.key) ?? (f.type === 'checkbox' ? 0 : '');
    setDraft(d);
    setErr(null);
    setEditing(true);
    const detailEndpoint = EDIT_DETAIL_ENDPOINT[type];
    setDocumentDetail(null);
    if (detailEndpoint) {
      setDetailLoading(true);
      api.detail<Record<string, unknown>>(`${detailEndpoint}/${row.id}`)
        .then(setDocumentDetail)
        .catch((e) => setErr(e))
        .finally(() => setDetailLoading(false));
    }
    // 物料组来自物料组主数据，不是自由文本。只在打开物料编辑框时加载，
    // 避免列表中的每一行都单独请求一次。
    if (type === 'items') {
      api.list<string>('/api/item-groups')
        .then(setItemGroups)
        .catch((e) => setErr(e));
    }
  };

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {};
      for (const f of fields) {
        const v = draft[f.key];
        if (v === undefined) continue;
        body[f.key] = f.type === 'checkbox' ? (v ? 1 : 0) : v;
      }
      await api.patch(`/api/records/${type}/${row.id}`, body);
      setEditing(false);
      onDone();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.del(`/api/records/${type}/${row.id}`);
      setDeleting(false);
      onDone();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  const errText = err instanceof Error ? err.message : err ? String(err) : null;

  return (
    <>
      <div className="row-actions" onClick={(e) => e.stopPropagation()}>
        {!hideEdit ? <button className="btn link" onClick={startEdit} title={`编辑${label}`}>编辑</button> : null}
        <button className="btn link row-del" onClick={() => { setErr(null); setDeleting(true); }} title={`删除${label}`}>删除</button>
      </div>

      {editing ? createPortal(
        <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) setEditing(false); }}>
          <div className="modal" role="dialog" aria-modal="true" aria-label={`编辑${label}`} style={{ width: 'min(900px,94vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-head">编辑{label}</div>
            <div className="modal-body" style={{ minHeight: 0, overflowY: 'auto' }}>
              {errText ? <div className="notice bad" style={{ marginBottom: 12 }}>{errText}</div> : null}
              {context.length ? <div className="kv-grid" style={{ marginBottom: 12 }}>{context.map((item) => <div className="kv" key={item.label}><div className="k">{item.label}</div><div className="v">{String(item.value ?? '—')}</div></div>)}</div> : null}
              {editNote ? <div className="notice" style={{ marginBottom: 12 }}>{editNote}</div> : null}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: 12 }}>
                {fields.map((f) => (
                  <label className="field" key={f.key}>
                    <label>{f.label}{f.required ? ' *' : ''}</label>
                    {type === 'items' && f.key === 'item_group' ? (
                      <select
                        className="sel"
                        value={String(draft[f.key] ?? '')}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                      >
                        <option value="">请选择物料组</option>
                        {String(draft[f.key] ?? '') && !itemGroups.includes(String(draft[f.key]))
                          ? <option value={String(draft[f.key])}>当前旧分组：{String(draft[f.key])}</option>
                          : null}
                        {itemGroups.map((group) => <option key={group} value={group}>{group}</option>)}
                      </select>
                    ) : f.type === 'select' ? (
                      <select
                        className="sel"
                        value={String(draft[f.key] ?? '')}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                      >
                        <option value="">—</option>
                        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : f.type === 'checkbox' ? (
                      <input
                        type="checkbox"
                        checked={!!draft[f.key]}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.checked ? 1 : 0 }))}
                        style={{ width: 16, height: 16, marginTop: 6 }}
                      />
                    ) : (
                      <input
                        className="txt"
                        type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                        value={String(draft[f.key] ?? '')}
                        onChange={(e) => setDraft((d) => ({
                          ...d,
                          [f.key]: f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value,
                        }))}
                      />
                    )}
                  </label>
                ))}
              </div>
              {EDIT_DETAIL_ENDPOINT[type] ? (() => {
                const lines = Array.isArray(documentDetail?.lines) ? documentDetail.lines as Record<string, unknown>[] : [];
                const charges = Array.isArray(documentDetail?.charges) ? documentDetail.charges as Record<string, unknown>[] : [];
                const party = documentDetail?.customer_name ?? documentDetail?.supplier_name;
                return (
                  <div style={{ marginTop: 18, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>单据详细内容</div>
                    {detailLoading ? <div className="muted">正在读取单据明细…</div> : null}
                    {!detailLoading && documentDetail ? (
                      <>
                        <div className="kv-grid" style={{ marginBottom: 12 }}>
                          {party ? <div className="kv"><div className="k">客户 / 供应商</div><div className="v">{detailText(party)}</div></div> : null}
                          {documentDetail.currency ? <div className="kv"><div className="k">币种</div><div className="v">{detailText(documentDetail.currency)}</div></div> : null}
                          {documentDetail.grand_total !== undefined ? <div className="kv"><div className="k">含税总额</div><div className="v num">{detailText(documentDetail.grand_total)}</div></div> : null}
                          {documentDetail.estimated_total_ars !== undefined ? <div className="kv"><div className="k">预估本币</div><div className="v num">{detailText(documentDetail.estimated_total_ars)} ARS</div></div> : null}
                        </div>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>明细行（{lines.length}）· 行项目在此查看，仍通过单据草稿编辑流程修改。</div>
                        {lines.length ? (
                          <div className="table-wrap">
                            <table className="grid" style={{ minWidth: 660 }}>
                              <thead><tr><th>物料 / 描述</th><th>数量</th><th>单位</th><th>已交 / 已收</th><th>单价</th><th>税率</th><th>金额</th></tr></thead>
                              <tbody>{lines.map((line, index) => (
                                <tr key={String(line.id ?? index)}>
                                  <td>{[line.item_code, line.item_name, line.description_es, line.description].filter(Boolean).map(String).join(' · ') || detailText(line.item_id)}</td>
                                  <td className="num">{detailText(line.quantity)}</td>
                                  <td>{detailText(line.uom)}</td>
                                  <td className="num">{line.delivered_quantity !== undefined ? detailText(line.delivered_quantity) : line.received_quantity !== undefined ? detailText(line.received_quantity) : '—'}</td>
                                  <td className="num">{detailText(line.unit_price)}</td>
                                  <td className="num">{line.tax_rate === undefined ? '—' : `${detailText(line.tax_rate)}%`}</td>
                                  <td className="num">{detailText(line.line_total)}</td>
                                </tr>
                              ))}</tbody>
                            </table>
                          </div>
                        ) : <div className="muted">该单据没有明细行。</div>}
                        {charges.length ? (
                          <div style={{ marginTop: 12 }}>
                            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>进口 / 附加费用（{charges.length}）</div>
                            <div className="table-wrap"><table className="grid" style={{ minWidth: 440 }}>
                              <thead><tr><th>费用</th><th>计费基数</th><th>比例</th><th>金额</th></tr></thead>
                              <tbody>{charges.map((charge, index) => <tr key={String(charge.id ?? index)}>
                                <td>{detailText(charge.description ?? charge.charge_type)}</td><td className="num">{detailText(charge.calculation_base)}</td><td className="num">{charge.rate === undefined ? '—' : `${detailText(charge.rate)}%`}</td><td className="num">{detailText(charge.amount)} {detailText(charge.currency)}</td>
                              </tr>)}</tbody>
                            </table></div>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                );
              })() : null}
            </div>
            <div className="modal-foot">
              <div className="spacer" />
              <button className="btn" onClick={() => setEditing(false)} disabled={busy}>取消</button>
              <button className="btn primary" onClick={save} disabled={busy}>{busy ? '保存中…' : '保存修改'}</button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      <ConfirmDialog
        open={deleting}
        title={`删除${label}`}
        message={`确认删除「${label}」？该操作不可恢复，并写入审计日志。`}
        detail={<DeleteBlockedDetail error={err} />}
        tone="danger"
        confirmLabel="确认删除"
        busy={busy}
        onConfirm={remove}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}

// 纯删除按钮：给「只有删除通道、没有可编辑字段」的资料用（如维修履历 asset-service-cycles ——
// 它不在 EDITABLE_RECORDS 里，走 RecordActions 的编辑弹窗只会 400「该资料不支持编辑」）。
// 路由、权限、审计与 RecordActions 完全一致：DELETE /api/records/:type/:id。
// 默认仅管理员可见；传 minRoles 时按「管理员 + 这些岗位角色」渲染（后端路由对
// asset-service-cycles 已同步放开 BIZ_ROLES，与 /api/repair 写权限同源）。
export function DeleteRecordButton({ type, id, label, note, minRoles, onDone }: {
  type: string;
  id: string;
  label: string;
  /** 补充说明这次删除会连带处理什么，避免管理员误判影响面。 */
  note?: string;
  /** 除管理员外允许删除的岗位角色（与后端路由放开范围严格一致；不传=仅管理员）。 */
  minRoles?: readonly string[];
  onDone: () => void;
}) {
  const { user, isAdmin } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  // 保留原始错误对象：ApiError.details.blockers 是 409 拦截详情的数据源，降级成字符串会丢
  const [err, setErr] = useState<unknown>(null);

  const allowed = isAdmin || (!!minRoles?.length && minRoles.some((r) => rolesOf(user).includes(r as Role)));
  if (!allowed) return null;

  const remove = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.del(`/api/records/${type}/${id}`);
      setDeleting(false);
      onDone();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className="btn link row-del"
        onClick={() => { setErr(null); setDeleting(true); }}
        title={`删除${label}`}
      >删除</button>
      <ConfirmDialog
        open={deleting}
        title={`删除${label}`}
        message={`确认删除「${label}」？该操作不可恢复，并写入审计日志。${note ?? ''}`}
        detail={<DeleteBlockedDetail error={err} />}
        tone="danger"
        confirmLabel="确认删除"
        busy={busy}
        onConfirm={remove}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}
